import { useState } from 'react'

import {
  useFeaturePageImageMissingApi,
  useFeaturePageImageSetApi
} from '@/common/api/modules/featurePageImage'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import TextField from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import MessageBar, { MessageType } from '@/components/view/molecules/bar/MessageBar'

interface Props {
  siteId: number
}

/**
 * 特集ページの画像が取得できず、手動保管が必要なページの案内。
 *
 * V5区分のサイトは W2Commerce がページのコンテンツと画像パスをファイルで持つため
 * マスタ連携で画像が届かず、公開ページから取得している。取得できなかったページは
 * ここから手動で登録する。
 *
 * 対象外のサイト（V6 / Media-DRIVER 無効）ではAPIが0件を返すため、何も表示しない。
 */
const FeaturePageImageAlert = ({ siteId }: Props) => {
  const { data, mutate } = useFeaturePageImageMissingApi({ site_id: siteId })
  const [opened, setOpened] = useState(false)
  // 行ごとの入力値と、いま登録中の対象。フック単位の isMutating を行に当てると全行が反応するため
  const [inputs, setInputs] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState<string | null>(null)

  const { trigger: setImage } = useFeaturePageImageSetApi(() => {
    setSaving(null)
    mutate()
  })

  if (!data || data.count === 0) return null

  const save = (featurePageId: string) => {
    const url = (inputs[featurePageId] || '').trim()
    if (!url) return
    setSaving(featurePageId)
    setImage({ site_id: siteId, feature_page_id: featurePageId, image_url: url })
  }

  return (
    <div className='mb-3'>
      <MessageBar
        type={MessageType.WARNING}
        linkText={opened ? '閉じる' : '対象を表示'}
        onClickLink={() => setOpened((v) => !v)}
        center
      >
        画像を取得できなかった特集ページが {data.count.toLocaleString()}件 あります。
        画像を手動で登録してください。
      </MessageBar>

      {opened && (
        <Area noShadow>
          <div className='flex flex-col gap-2'>
            {data.pages.map((p) => (
              <ItemAlign key={p.feature_page_id} align='center' gapX={8}>
                <Text size={12}>{p.feature_page_id}</Text>
                <Text size={12}>{p.title || '(タイトルなし)'}</Text>
                {p.page_url && (
                  <a
                    href={p.page_url}
                    target='_blank'
                    rel='noreferrer'
                    className='text-emphasis text-[12px] underline'
                  >
                    ページを開く
                  </a>
                )}
                <TextField
                  value={inputs[p.feature_page_id] || ''}
                  placeholder='画像URL'
                  onChange={(e) =>
                    setInputs((s) => ({ ...s, [p.feature_page_id]: e.target.value }))
                  }
                />
                <Button
                  color={ButtonColor.SECONDARY}
                  loading={saving === p.feature_page_id}
                  disabled={
                    saving === p.feature_page_id || !(inputs[p.feature_page_id] || '').trim()
                  }
                  onClick={() => save(p.feature_page_id)}
                >
                  登録
                </Button>
              </ItemAlign>
            ))}
          </div>
          <Text size={12} color={TextColor.SECONDARY}>
            登録した画像は、取得バッチによって消されることはありません。
          </Text>
        </Area>
      )}
    </div>
  )
}

export default FeaturePageImageAlert
